import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SecretField,
  Alert,
  AppShell,
  Button,
  Card,
  Checkbox,
  Dialog,
  Field,
  Grid,
  Inline,
  SelectField,
  Stack,
  StatusBadge,
  TextAreaField,
  ThemeProvider,
  ToastProvider,
  formatMoneyMinor,
  majorInputToMinor
} from "@leylion/design-system";
import type { NavItem } from "@leylion/design-system";
import {
  addOrderLine,
  changeOwnPassword,
  clearManagerApprovalGate,
  clearSession,
  clearStoredContext,
  createOrder,
  getAccessToken,
  deleteOrderLine,
  getContextOptions,
  getStoredManagerApprovalGate,
  getOrderCart,
  getPosCatalog,
  getStoredPermissions,
  getOpenOrders,
  getOpenShift,
  getOrder,
  getStoredContext,
  login,
  openShift,
  selectContext,
  setAccessToken,
  setStoredContext,
  setStoredPermissions,
  updateOrder,
  updateOrderLine,
  getRuntimeSettings,
  getApprovalQueueSummary,
  prepareStoredContextForPermissionRevision,
  setStoredManagerApprovalGate,
  verifyManagerApprovalPin
} from "./api/client";
import type { ContextOption, Order, ServiceType, Shift } from "./api/types";
import type { RuntimeSettings } from "./api/settings-types";
import type { ManagerApprovalGate } from "./api/identity-types";
import type { OrderCart, PosCatalog, PosProduct } from "./api/catalog-types";
import { SettingsCenter } from "./settings/SettingsCenter";
import { TeamCenter } from "./team/TeamCenter";
import { ManagerApprovalsCenter } from "./team/ManagerApprovalsCenter";
import { CatalogCenter } from "./catalog/CatalogCenter";
import { CashierWorkspace } from "./cashier/CashierWorkspace";
import { InventoryPurchasingCenter } from "./inventory/InventoryPurchasingCenter";
import { RestaurantOperationsCenter } from "./restaurant/RestaurantOperationsCenter";
import { CrmLoyaltyCenter } from "./crm/CrmLoyaltyCenter";
import { ReportsFinanceCenter } from "./reports/ReportsFinanceCenter";
import { IntegrationsComplianceCenter } from "./integrations/IntegrationsComplianceCenter";
import { DeviceOperationsCenter } from "./devices/DeviceOperationsCenter";
import { ProductionOperationsCenter } from "./production/ProductionOperationsCenter";
import { ReleaseControlCenter } from "./release/ReleaseControlCenter";
import { BRAND, LEGACY_STORAGE_KEYS, STORAGE_KEYS } from "./brand";

type Language = "ar" | "en";
type SaveState = "idle" | "saving" | "saved" | "error";
type AppPage = "pos" | "approvals" | "restaurant" | "crm" | "reports" | "integrations" | "devices" | "production" | "release" | "settings" | "team" | "catalog" | "inventory";

const copy = {
  ar: {
    signIn: "تسجيل الدخول",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    mfa: "رمز التحقق",
    continue: "متابعة",
    chooseContext: "اختر المؤسسة والفرع",
    branch: "المؤسسة والفرع",
    select: "اختيار",
    workspace: "مساحة التشغيل",
    shift: "الوردية",
    noShift: "لا توجد وردية مفتوحة",
    openingCash: "رصيد افتتاح الصندوق",
    openShift: "فتح الوردية",
    newOrder: "طلب جديد",
    serviceType: "نوع الطلب",
    note: "ملاحظات الطلب",
    create: "إنشاء الطلب",
    openOrders: "الطلبات المفتوحة",
    noOrders: "لا توجد طلبات مفتوحة.",
    restore: "استعادة",
    currentOrder: "الطلب الحالي",
    autoSave: "الحفظ التلقائي",
    saving: "جارٍ الحفظ...",
    saved: "تم الحفظ",
    saveError: "فشل الحفظ",
    logout: "تسجيل الخروج",
    switchContext: "تغيير الفرع",
    foundation: "Production Foundation",
    subtitle: "مسار حقيقي على .NET 10 وReact وPostgreSQL مع RLS",
    mfaHint: "رمز التطوير المحلي: 123456",
    shiftOpen: "الوردية مفتوحة",
    lastUpdated: "آخر تحديث"
  },
  en: {
    signIn: "Sign in",
    email: "Email",
    password: "Password",
    mfa: "Verification code",
    continue: "Continue",
    chooseContext: "Choose organization and branch",
    branch: "Organization and branch",
    select: "Select",
    workspace: "Operations workspace",
    shift: "Shift",
    noShift: "No open shift",
    openingCash: "Opening cash",
    openShift: "Open shift",
    newOrder: "New order",
    serviceType: "Service type",
    note: "Order note",
    create: "Create order",
    openOrders: "Open orders",
    noOrders: "No open orders.",
    restore: "Restore",
    currentOrder: "Current order",
    autoSave: "Auto-save",
    saving: "Saving...",
    saved: "Saved",
    saveError: "Save failed",
    logout: "Log out",
    switchContext: "Switch branch",
    foundation: "Production Foundation",
    subtitle: "Real .NET 10, React and PostgreSQL path with RLS",
    mfaHint: "Local development code: 123456",
    shiftOpen: "Shift is open",
    lastUpdated: "Last updated"
  }
} as const;

const serviceTypes: ServiceType[] = ["WalkIn", "DineIn", "Takeaway", "Delivery"];
const applicationMode = import.meta.env.VITE_APP_MODE === "cashier" ? "cashier" : "full";
const managerApprovalPermissionRevision = "20260726-manager-approval-effective-rbac-pin-v5";
const appPages = new Set<AppPage>([
  "pos",
  "approvals",
  "restaurant",
  "crm",
  "reports",
  "integrations",
  "devices",
  "production",
  "release",
  "settings",
  "team",
  "catalog",
  "inventory"
]);

prepareStoredContextForPermissionRevision(managerApprovalPermissionRevision);

function hasPermission(permissions: string[], permission: string): boolean {
  return permissions.some((item) => item.toLowerCase() === permission.toLowerCase());
}

function isManagerRole(roleName: string | undefined): boolean {
  const normalized = roleName?.trim().toLowerCase();
  return normalized === "owner" || normalized === "manager";
}

function isManagerApprovalGateValid(gate: ManagerApprovalGate | null): gate is ManagerApprovalGate {
  return Boolean(
    gate?.accessToken
    && !Number.isNaN(Date.parse(gate.expiresAt))
    && Date.parse(gate.expiresAt) > Date.now() + 5_000
  );
}

function readPageFromLocation(): AppPage {
  const route = window.location.hash
    .replace(/^#\/?/, "")
    .split(/[/?]/, 1)[0]
    ?.trim()
    .toLowerCase();

  if (route === "manager-approvals" || route === "pending-approvals") {
    return "approvals";
  }

  return appPages.has(route as AppPage) ? route as AppPage : "pos";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected error.";
}

export function App() {
  const [language, setLanguage] = useState<Language>("ar");
  const [token, setToken] = useState<string | null>(() => getAccessToken());
  const [context, setContext] = useState<ContextOption | null>(() => getStoredContext());
  const [page, setPage] = useState<AppPage>(() => readPageFromLocation());
  const [permissions, setPermissions] = useState<string[]>(() => getStoredPermissions());
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [managerGate, setManagerGate] = useState<ManagerApprovalGate | null>(() =>
    getStoredManagerApprovalGate()
  );
  const [managerPinOpen, setManagerPinOpen] = useState(false);
  const [managerPin, setManagerPin] = useState("");
  const [managerPinError, setManagerPinError] = useState("");
  const [managerPinBusy, setManagerPinBusy] = useState(false);

  const text = copy[language];
  const direction = language === "ar" ? "rtl" : "ltr";
  const canReadApprovals = hasPermission(permissions, "approvals.read");
  const showManagerApprovals = canReadApprovals || isManagerRole(context?.roleName);
  const navigate = useCallback((target: AppPage, replace = false) => {
    setPage(target);
    const nextHash = target === "approvals" ? "#/manager-approvals" : `#/${target}`;
    if (window.location.hash === nextHash) return;

    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
    if (replace) {
      window.history.replaceState(null, "", nextUrl);
    } else {
      window.history.pushState(null, "", nextUrl);
    }
  }, []);

  const requestManagerPin = useCallback((message = "") => {
    clearManagerApprovalGate();
    setManagerGate(null);
    setManagerPin("");
    setManagerPinError(message);
    setManagerPinOpen(true);
  }, []);

  const openManagerApprovals = useCallback(() => {
    if (isManagerApprovalGateValid(managerGate)) {
      navigate("approvals");
      return;
    }

    requestManagerPin();
  }, [managerGate, navigate, requestManagerPin]);

  const handleManagerGateInvalid = useCallback(() => {
    navigate("pos", true);
    requestManagerPin(
      language === "ar"
        ? "انتهت صلاحية تحقق رمز المدير. أدخل الرمز مرة أخرى."
        : "Manager PIN verification expired. Enter the PIN again."
    );
  }, [language, navigate, requestManagerPin]);

  const submitManagerPin = useCallback(async () => {
    if (!/^\d{4,8}$/.test(managerPin)) {
      setManagerPinError(
        language === "ar"
          ? "أدخل رمز مدير رقميًا من 4 إلى 8 خانات."
          : "Enter a 4 to 8 digit manager PIN."
      );
      return;
    }

    try {
      setManagerPinBusy(true);
      setManagerPinError("");
      const gate = await verifyManagerApprovalPin(managerPin);
      setStoredManagerApprovalGate(gate);
      setManagerGate(gate);
      setManagerPin("");
      setManagerPinOpen(false);
      navigate("approvals");
    } catch (caught) {
      setManagerPinError(errorMessage(caught));
    } finally {
      setManagerPinBusy(false);
    }
  }, [language, managerPin, navigate]);

  const fullNavigation: NavItem[] = [
    {
      id: "pos",
      label: language === "ar" ? "نقطة البيع" : "Point of Sale",
      icon: "orders",
      active: page === "pos",
      onClick: () => navigate("pos")
    },
    ...(showManagerApprovals
      ? [{
          id: "approvals",
          label: language === "ar"
            ? `موافقات المدير${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`
            : `Manager Approvals${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`,
          icon: "activity" as const,
          active: page === "approvals",
          onClick: openManagerApprovals
        }]
      : []),
    ...(permissions.includes("inventory.read")
      ? [{
          id: "inventory",
          label:
            language === "ar"
              ? "المخزون والمشتريات"
              : "Inventory & Purchasing",
          icon: "inventory" as const,
          active: page === "inventory",
          onClick: () => navigate("inventory")
        }]
      : []),
    ...(permissions.includes("kds.read")
      || permissions.includes("reservations.manage")
      || permissions.includes("delivery.read")
      || permissions.includes("recipes.manage")
      ? [{
          id: "restaurant",
          label:
            language === "ar"
              ? "المطعم والمطبخ"
              : "Restaurant & Kitchen",
          icon: "activity" as const,
          active: page === "restaurant",
          onClick: () => navigate("restaurant")
        }]
      : []),
    ...(permissions.includes("customers.read")
      ? [{
          id: "crm",
          label: language === "ar" ? "العملاء وCRM" : "Customers & CRM",
          icon: "user" as const,
          active: page === "crm",
          onClick: () => navigate("crm")
        }]
      : []),
    ...(permissions.includes("analytics.read")
      || permissions.includes("reports.sales.read")
      || permissions.includes("reports.inventory.read")
      || permissions.includes("reports.finance.read")
      ? [{
          id: "reports",
          label:
            language === "ar"
              ? "التقارير والمالية"
              : "Reports & Finance",
          icon: "reports" as const,
          active: page === "reports",
          onClick: () => navigate("reports")
        }]
      : []),
    ...(permissions.includes("integrations.read")
      || permissions.includes("compliance.read")
      || permissions.includes("einvoice.read")
      ? [{
          id: "integrations",
          label:
            language === "ar"
              ? "التكاملات والامتثال"
              : "Integrations & Compliance",
          icon: "activity" as const,
          active: page === "integrations",
          onClick: () => navigate("integrations")
        }]
      : []),
    ...(permissions.includes("devices.read")
      ? [{
          id: "devices",
          label: language === "ar" ? "الأجهزة والهواتف" : "Devices & Mobile",
          icon: "activity" as const,
          active: page === "devices",
          onClick: () => navigate("devices")
        }]
      : []),
    ...(permissions.includes("production.read")
      ? [{
          id: "production",
          label: language === "ar" ? "مركز الإنتاج" : "Production Control",
          icon: "activity" as const,
          active: page === "production",
          onClick: () => navigate("production")
        }]
      : []),
    ...(permissions.includes("release.read")
      ? [{
          id: "release",
          label: language === "ar" ? "الإصدار وUAT" : "Release & UAT",
          icon: "activity" as const,
          active: page === "release",
          onClick: () => navigate("release")
        }]
      : []),
    ...(permissions.includes("catalog.read")
      ? [{
          id: "catalog",
          label: language === "ar" ? "المنتجات والتسعير" : "Catalog & Pricing",
          icon: "box" as const,
          active: page === "catalog",
          onClick: () => navigate("catalog")
        }]
      : []),
    ...(permissions.includes("security.read")
      ? [{
          id: "team",
          label: language === "ar" ? "الفريق والأمان" : "Team & Security",
          icon: "user" as const,
          active: page === "team",
          onClick: () => navigate("team")
        }]
      : []),
    ...(permissions.includes("settings.read")
      ? [{
          id: "settings",
          label: language === "ar" ? "الإعدادات" : "Settings",
          icon: "settings" as const,
          active: page === "settings",
          onClick: () => navigate("settings")
        }]
      : [])
  ];
  const navigation = applicationMode === "cashier"
    ? fullNavigation.filter((item) => item.id === "pos" || item.id === "approvals")
    : fullNavigation;

  useEffect(() => {
    const syncPageFromLocation = () => setPage(readPageFromLocation());
    window.addEventListener("hashchange", syncPageFromLocation);
    window.addEventListener("popstate", syncPageFromLocation);
    return () => {
      window.removeEventListener("hashchange", syncPageFromLocation);
      window.removeEventListener("popstate", syncPageFromLocation);
    };
  }, []);

  useEffect(() => {
    if (!token || !context || page !== "approvals") return;

    // Cashier mode uses privilege elevation: the cashier keeps the POS session
    // while a server-verified Owner/Manager PIN grants a short-lived approvals gate.
    // Do not require approvals.read on the cashier's own role after the gate exists.
    if (applicationMode === "cashier") {
      if (!isManagerApprovalGateValid(managerGate)) {
        navigate("pos", true);
        requestManagerPin();
      }
      return;
    }

    if (!showManagerApprovals) {
      navigate("pos", true);
      return;
    }

    if (!isManagerApprovalGateValid(managerGate)) {
      navigate("pos", true);
      requestManagerPin();
    }
  }, [token, context, page, showManagerApprovals, managerGate, navigate, requestManagerPin]);

  useEffect(() => {
    if (!managerGate) return;

    const milliseconds = Date.parse(managerGate.expiresAt) - Date.now();
    if (milliseconds <= 0) {
      clearManagerApprovalGate();
      setManagerGate(null);
      return;
    }

    const timer = window.setTimeout(() => {
      clearManagerApprovalGate();
      setManagerGate(null);
      if (page === "approvals") {
        navigate("pos", true);
        requestManagerPin(
          language === "ar"
            ? "انتهت صلاحية تحقق رمز المدير. أدخل الرمز مرة أخرى."
            : "Manager PIN verification expired. Enter the PIN again."
        );
      }
    }, milliseconds);

    return () => window.clearTimeout(timer);
  }, [managerGate, page, language, navigate, requestManagerPin]);

  useEffect(() => {
    // The role-name fallback keeps the navigation entry discoverable while a
    // repaired token is issued, but only an explicit permission may start the
    // background queue poll. This keeps a non-critical badge request isolated
    // from cashier startup.
    if (!token || !context || !canReadApprovals || applicationMode === "cashier") {
      // The cashier shell deliberately does not poll a manager-only badge.
      // Approvals are loaded after the explicit Manager PIN gate, so a degraded
      // approval subsystem can never block shift startup on port 8081.
      setPendingApprovalCount(0);
      return;
    }

    let cancelled = false;
    const refreshPendingApprovals = async () => {
      try {
        const summary = await getApprovalQueueSummary();
        if (!cancelled) {
          setPendingApprovalCount(summary.pendingOperationalRequests);
        }
      } catch {
        if (!cancelled) setPendingApprovalCount(0);
      }
    };

    void refreshPendingApprovals();
    const timer = window.setInterval(() => void refreshPendingApprovals(), 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [token, context, canReadApprovals]);

  useEffect(() => {
    document.title = applicationMode === "cashier"
      ? `${BRAND.productName} Cashier`
      : `${BRAND.productName} Enterprise`;
  }, []);

  const logout = () => {
    clearSession();
    setToken(null);
    setContext(null);
    setPermissions([]);
    setPendingApprovalCount(0);
    setManagerGate(null);
    setManagerPinOpen(false);
    setManagerPin("");
    setManagerPinError("");
    navigate("pos", true);
  };

  const switchContext = () => {
    clearStoredContext();
    localStorage.removeItem(LEGACY_STORAGE_KEYS.selectedContext);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.permissions);
    clearManagerApprovalGate();
    setContext(null);
    setPermissions([]);
    setPendingApprovalCount(0);
    setManagerGate(null);
    setManagerPinOpen(false);
    setManagerPin("");
    setManagerPinError("");
    navigate("pos", true);
  };

  return (
    <ThemeProvider
      direction={direction}
      theme={applicationMode === "cashier" ? "dark" : "light"}
      density="comfortable"
      className={`leylion-app-mode leylion-app-page--${page}${
        applicationMode === "cashier"
          ? ` cashier-app-mode cashier-app-page--${page}${showManagerApprovals ? " cashier-app-mode--manager" : ""}`
          : ""
      }`}
    >
      <ToastProvider>
        {token && context ? (
          <AppShell
            brand={<img className="leylion-brand-image leylion-brand-image--sidebar" src={BRAND.assets.wordmarkLight} alt={BRAND.productName} />}
            navigation={navigation}
            topbar={
              <div className="product-topbar">
                <div>
                  <strong>{context.tenantName}</strong>
                  <small>{context.branchName} · {context.roleName}</small>
                </div>
                <div className="topbar__actions">
                  <span className="context-chip">{context.branchName}</span>
                  {showManagerApprovals ? (
                    <Button
                      variant={page === "approvals" || pendingApprovalCount > 0 ? "primary" : "secondary"}
                      type="button"
                      data-testid="open-manager-approvals"
                      onClick={openManagerApprovals}
                    >
                      {language === "ar"
                        ? `موافقات المدير${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`
                        : `Manager Approvals${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`}
                    </Button>
                  ) : null}
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
                  >
                    {language === "ar" ? "EN" : "AR"}
                  </Button>
                  <Button variant="secondary" type="button" onClick={switchContext}>
                    {text.switchContext}
                  </Button>
                  <Button variant="secondary" type="button" onClick={logout}>
                    {text.logout}
                  </Button>
                </div>
              </div>
            }
          >
            {page === "pos" ? (
              <CashierWorkspace
                language={language}
                context={context}
                permissions={permissions}
                onSessionExpired={logout}
                pendingApprovalCount={pendingApprovalCount}
                onOpenManagerApprovals={
                  applicationMode === "cashier"
                    ? openManagerApprovals
                    : showManagerApprovals
                      ? openManagerApprovals
                      : undefined
                }
                standaloneCashierMode={applicationMode === "cashier"}
                onToggleLanguage={() => setLanguage(language === "ar" ? "en" : "ar")}
                onSwitchContext={switchContext}
                onLogout={logout}
              />
            ) : page === "approvals" ? (
              isManagerApprovalGateValid(managerGate) ? (
                <ManagerApprovalsCenter
                  language={language}
                  context={context}
                  managerAccessToken={managerGate.accessToken}
                  managerAccessExpiresAt={managerGate.expiresAt}
                  canSelfDecide={managerGate.canSelfDecide}
                  permissions={permissions}
                  onGateInvalid={handleManagerGateInvalid}
                />
              ) : null
            ) : page === "inventory" ? (
              <InventoryPurchasingCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "restaurant" ? (
              <RestaurantOperationsCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "crm" ? (
              <CrmLoyaltyCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "reports" ? (
              <ReportsFinanceCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "integrations" ? (
              <IntegrationsComplianceCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "devices" ? (
              <DeviceOperationsCenter
                language={language}
                permissions={permissions}
                context={context}
              />
            ) : page === "production" ? (
              <ProductionOperationsCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "release" ? (
              <ReleaseControlCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "catalog" ? (
              <CatalogCenter
                language={language}
                permissions={permissions}
              />
            ) : page === "team" ? (
              <TeamCenter
                language={language}
                context={context}
                permissions={permissions}
                onOpenManagerApprovals={openManagerApprovals}
              />
            ) : (
              <SettingsCenter language={language} context={context} />
            )}
          </AppShell>
        ) : (
          <div className="app">
            <header className="topbar">
              <div className="brand" aria-label={BRAND.productName}>
                <img className="leylion-brand-image leylion-brand-image--header" src={BRAND.assets.wordmarkDark} alt={BRAND.productName} />
                <small>{applicationMode === "cashier" ? (language === "ar" ? "واجهة الكاشير" : "Cashier Workspace") : text.foundation}</small>
              </div>
              <div className="topbar__actions">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
                >
                  {language === "ar" ? "EN" : "AR"}
                </Button>
              </div>
            </header>

            <main className="page">
              {!token ? (
                <LoginScreen language={language} onAuthenticated={setToken} />
              ) : (
                <ContextScreen
                  token={token}
                  language={language}
                  onSelected={(selectedToken, selectedContext, responsePermissions) => {
                    setAccessToken(selectedToken);
                    setStoredContext(selectedContext);
                    setToken(selectedToken);
                    setContext(selectedContext);
                    setStoredPermissions(responsePermissions);
                    setPermissions(responsePermissions);
                    clearManagerApprovalGate();
                    setManagerGate(null);
                    const requestedPage = readPageFromLocation();
                    navigate("pos", true);
                    if (requestedPage === "approvals"
                        && (hasPermission(responsePermissions, "approvals.read")
                          || isManagerRole(selectedContext.roleName))) {
                      setManagerPinOpen(true);
                    }
                  }}
                  onSessionExpired={logout}
                />
              )}
            </main>
          </div>
        )}
        <Dialog
          open={Boolean(token && context && managerPinOpen)}
          onClose={() => {
            if (managerPinBusy) return;
            setManagerPinOpen(false);
            setManagerPin("");
            setManagerPinError("");
          }}
          title={language === "ar" ? "تحقق رمز المدير" : "Verify manager PIN"}
          footer={
            <Inline>
              <Button
                variant="secondary"
                type="button"
                disabled={managerPinBusy}
                onClick={() => {
                  setManagerPinOpen(false);
                  setManagerPin("");
                  setManagerPinError("");
                }}
              >
                {language === "ar" ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                type="button"
                disabled={managerPinBusy || !/^\d{4,8}$/.test(managerPin)}
                onClick={() => void submitManagerPin()}
              >
                {managerPinBusy
                  ? language === "ar" ? "جارٍ التحقق…" : "Verifying…"
                  : language === "ar" ? "تحقق وافتح الموافقات" : "Verify and open approvals"}
              </Button>
            </Inline>
          }
        >
          <Stack>
            <Alert
              tone="warning"
              title={language === "ar" ? "منطقة إدارية محمية" : "Protected manager area"}
            >
              {language === "ar"
                ? "يتحقق الخادم من رمز المدير قبل عرض أي طلب أو تنفيذ موافقة. التحقق مؤقت ومرتبط بالحساب والفرع والدور والجلسة الحالية."
                : "The server verifies the manager PIN before any request is displayed or decision is executed. Access is temporary and bound to the current account, branch, role and session."}
            </Alert>
            <SecretField
              autoFocus
              data-testid="manager-approval-pin"
              label={language === "ar" ? "الرمز السري للمدير" : "Manager PIN"}
              revealLabel={language === "ar" ? "إظهار رمز المدير" : "Show manager PIN"}
              concealLabel={language === "ar" ? "إخفاء رمز المدير" : "Hide manager PIN"}
              inputMode="numeric"
              autoComplete="off"
              minLength={4}
              maxLength={8}
              value={managerPin}
              onChange={(event) =>
                setManagerPin(event.target.value.replace(/\D/g, "").slice(0, 8))
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" && /^\d{4,8}$/.test(managerPin) && !managerPinBusy) {
                  event.preventDefault();
                  void submitManagerPin();
                }
              }}
            />
            {managerPinError ? (
              <Alert tone="danger" title={language === "ar" ? "تعذر التحقق" : "Verification failed"}>
                {managerPinError}
              </Alert>
            ) : null}
          </Stack>
        </Dialog>
      </ToastProvider>
    </ThemeProvider>
  );
}

function LoginScreen({
  language,
  onAuthenticated
}: {
  language: Language;
  onAuthenticated: (token: string) => void;
}) {
  const text = copy[language];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [passwordChangeToken, setPasswordChangeToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const response = await login(email, password, mfaCode);
      if (response.requiresMfa) {
        setMfaRequired(true);
        return;
      }

      if (!response.accessToken) {
        throw new Error("Authentication token was not returned.");
      }

      if (response.message === "PASSWORD_CHANGE_REQUIRED") {
        setPasswordChangeToken(response.accessToken);
        return;
      }

      setAccessToken(response.accessToken);
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onAuthenticated(response.accessToken);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };


  const submitPasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!passwordChangeToken) return;
    if (newPassword !== confirmPassword) {
      setError(language === "ar" ? "كلمتا المرور غير متطابقتين." : "Passwords do not match.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const response = await changeOwnPassword(
        passwordChangeToken,
        password,
        newPassword
      );
      if (!response.accessToken) {
        throw new Error("Updated authentication token was not returned.");
      }
      setAccessToken(response.accessToken);
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordChangeToken(null);
      onAuthenticated(response.accessToken);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-layout">
      <Card className="auth-card">
        <img className="leylion-brand-image leylion-brand-image--login" src={BRAND.assets.wordmarkDark} alt={BRAND.productName} />
        <div className="eyebrow">{BRAND.loginEyebrow}</div>
        <h1>{text.signIn}</h1>
        <p>{text.subtitle}</p>

        {passwordChangeToken ? (
          <form onSubmit={submitPasswordChange}>
            <Alert
              tone="warning"
              title={language === "ar" ? "تغيير كلمة المرور مطلوب" : "Password change required"}
            >
              {language === "ar"
                ? "هذا حساب جديد أو تمت إعادة تعيين كلمة مروره. اختر كلمة مرور شخصية قبل متابعة الدخول."
                : "This is a new or reset account. Choose a personal password before continuing."}
            </Alert>
            <SecretField
              data-testid="new-password"
              label={language === "ar" ? "كلمة المرور الجديدة" : "New password"}
              revealLabel={language === "ar" ? "إظهار كلمة المرور الجديدة" : "Show new password"}
              concealLabel={language === "ar" ? "إخفاء كلمة المرور الجديدة" : "Hide new password"}
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
            <SecretField
              data-testid="confirm-password"
              label={language === "ar" ? "تأكيد كلمة المرور" : "Confirm password"}
              revealLabel={language === "ar" ? "إظهار تأكيد كلمة المرور" : "Show password confirmation"}
              concealLabel={language === "ar" ? "إخفاء تأكيد كلمة المرور" : "Hide password confirmation"}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
            {error ? <div className="error-banner" role="alert">{error}</div> : null}
            <Button data-testid="password-change-submit" disabled={busy} type="submit">
              {busy ? "…" : language === "ar" ? "تغيير ومتابعة" : "Change & continue"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submit}>
            <Field
              data-testid="email"
              label={text.email}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <SecretField
              data-testid="password"
              label={text.password}
              revealLabel={language === "ar" ? "إظهار كلمة المرور" : "Show password"}
              concealLabel={language === "ar" ? "إخفاء كلمة المرور" : "Hide password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            {mfaRequired ? (
              <>
                <Field
                  data-testid="mfa-code"
                  label={text.mfa}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                  required
                />
                <p className="hint">{text.mfaHint}</p>
              </>
            ) : null}
            {error ? <div className="error-banner" role="alert">{error}</div> : null}
            <Button data-testid="login-submit" disabled={busy} type="submit">
              {busy ? "…" : text.continue}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}

function ContextScreen({
  token,
  language,
  onSelected,
  onSessionExpired
}: {
  token: string;
  language: Language;
  onSelected: (token: string, context: ContextOption, permissions: string[]) => void;
  onSessionExpired: () => void;
}) {
  const text = copy[language];
  const [options, setOptions] = useState<ContextOption[]>([]);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    getContextOptions(token)
      .then((items) => {
        setOptions(items);
        setSelected(items[0] ? `${items[0].tenantId}|${items[0].branchId}` : "");
      })
      .catch((caught: unknown) => {
        setError(errorMessage(caught));
        if ((caught as { status?: number }).status === 401) {
          onSessionExpired();
        }
      });
  }, [token, onSessionExpired]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const [tenantId, branchId] = selected.split("|");
    const response = await selectContext(token, tenantId, branchId);
    onSelected(response.accessToken, response.context, response.permissions);
  };

  return (
    <div className="auth-layout">
      <Card className="auth-card">
        <div className="eyebrow">TENANT & BRANCH CONTEXT</div>
        <h1>{text.chooseContext}</h1>
        <form onSubmit={submit}>
          <SelectField
            data-testid="context-select"
            label={text.branch}
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
            required
          >
            {options.map((option) => (
              <option
                key={`${option.tenantId}-${option.branchId}`}
                value={`${option.tenantId}|${option.branchId}`}
              >
                {option.tenantName} — {option.branchName} ({option.roleName})
              </option>
            ))}
          </SelectField>
          {error ? <div className="error-banner" role="alert">{error}</div> : null}
          <Button data-testid="context-submit" disabled={!selected} type="submit">
            {text.select}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function Workspace({
  language,
  context,
  onSessionExpired
}: {
  language: Language;
  context: ContextOption;
  onSessionExpired: () => void;
}) {
  const text = copy[language];
  const [shift, setShift] = useState<Shift | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [openingCash, setOpeningCash] = useState("0");
  const [runtimeSettings, setRuntimeSettings] = useState<RuntimeSettings | null>(null);
  const [posCatalog, setPosCatalog] = useState<PosCatalog | null>(null);
  const [cart, setCart] = useState<OrderCart | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [configProduct, setConfigProduct] = useState<PosProduct | null>(null);
  const [configVariantId, setConfigVariantId] = useState("");
  const [configOptionIds, setConfigOptionIds] = useState<string[]>([]);
  const [manualPriceInput, setManualPriceInput] = useState("");
  const [newServiceType, setNewServiceType] = useState<ServiceType>("Takeaway");
  const [newNote, setNewNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [error, setError] = useState("");
  const saveSequence = useRef(0);

  const refresh = async () => {
    try {
      const [openShiftValue, openOrderValues, runtime, catalog] = await Promise.all([
        getOpenShift(),
        getOpenOrders(),
        getRuntimeSettings(context.branchId),
        getPosCatalog(context.branchId)
      ]);
      setShift(openShiftValue);
      setOrders(openOrderValues);
      setRuntimeSettings(runtime);
      setPosCatalog(catalog);
      setSelectedCategoryId((value) => value || catalog.categories[0]?.id || "");
      setNewServiceType(runtime.defaultServiceType);
    } catch (caught) {
      if ((caught as { status?: number }).status === 401) {
        onSessionExpired();
        return;
      }
      setError(errorMessage(caught));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    if (!currentOrder || !dirty) {
      return;
    }

    const sequence = ++saveSequence.current;
    setSaveState("saving");

    const timer = window.setTimeout(async () => {
      try {
        const saved = await updateOrder(currentOrder);
        if (sequence !== saveSequence.current) {
          return;
        }

        setCurrentOrder(saved);
        setDirty(false);
        setSaveState("saved");
        setOrders((existing) => [
          saved,
          ...existing.filter((item) => item.id !== saved.id)
        ]);
      } catch (caught) {
        if (sequence === saveSequence.current) {
          setSaveState("error");
          setError(errorMessage(caught));
        }
      }
    }, runtimeSettings?.autoSaveDelayMs ?? 800);

    return () => window.clearTimeout(timer);
  }, [currentOrder, dirty, runtimeSettings?.autoSaveDelayMs]);

  const openNewShift = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      const openingCashMinor = majorInputToMinor(openingCash, runtimeSettings?.currency ?? "AED");
      if (openingCashMinor === null) {
        setError(language === "ar" ? "أدخل رصيد افتتاح صحيحاً." : "Enter a valid opening cash amount.");
        return;
      }
      const value = await openShift(openingCashMinor);
      setShift(value);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const startOrder = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      const created = await createOrder(newServiceType, newNote);
      setCurrentOrder(created);
      setCart(await getOrderCart(created.id));
      setOrders((existing) => [created, ...existing]);
      setNewNote("");
      setSaveState("saved");
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const restoreOrder = async (orderId: string) => {
    try {
      const restored = await getOrder(orderId);
      setCurrentOrder(restored);
      setCart(await getOrderCart(orderId));
      setDirty(false);
      setSaveState("saved");
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };


  const syncOrderFromCart = (nextCart: OrderCart) => {
    setCart(nextCart);
    setCurrentOrder((current) =>
      current
        ? {
            ...current,
            currency: nextCart.currency,
            subtotalMinor: nextCart.subtotalMinor,
            discountMinor: nextCart.discountMinor,
            netMinor: nextCart.netMinor,
            taxMinor: nextCart.taxMinor,
            totalMinor: nextCart.totalMinor,
            version: nextCart.version,
            updatedAt: new Date().toISOString()
          }
        : current
    );
  };

  const openProduct = (product: PosProduct) => {
    const defaultVariant =
      product.variants.find((variant) => variant.isDefault) ?? product.variants[0];
    const defaultOptions = product.modifierGroups.flatMap((group) =>
      group.options.filter((option) => option.isDefault).map((option) => option.id)
    );
    setConfigProduct(product);
    setConfigVariantId(defaultVariant?.id ?? "");
    setConfigOptionIds(defaultOptions);
    setManualPriceInput("");
  };

  const addConfiguredProduct = async () => {
    if (!currentOrder || !configProduct || !configVariantId) return;
    try {
      const manualUnitPriceMinor = manualPriceInput.trim()
        ? majorInputToMinor(manualPriceInput, currentOrder.currency)
        : null;
      if (manualPriceInput.trim() && manualUnitPriceMinor === null) {
        setError(language === "ar" ? "أدخل سعراً يدوياً صحيحاً." : "Enter a valid manual price.");
        return;
      }
      const nextCart = await addOrderLine(
        currentOrder.id,
        configVariantId,
        configOptionIds,
        currentOrder.version,
        manualUnitPriceMinor
      );
      syncOrderFromCart(nextCart);
      setConfigProduct(null);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const quantityStepForLine = (productId: string) =>
    Math.max(
      1,
      posCatalog?.products.find((product) => product.id === productId)?.quantityStepMilli ?? 1000
    );

  const changeLineQuantity = async (
    lineId: string,
    nextQuantityMilli: number,
    lineVersion: number,
    modifierOptionIds: string[]
  ) => {
    if (!currentOrder || !cart) return;
    try {
      if (nextQuantityMilli <= 0) {
        syncOrderFromCart(
          await deleteOrderLine(currentOrder.id, lineId, cart.version)
        );
        return;
      }
      syncOrderFromCart(
        await updateOrderLine(
          currentOrder.id,
          lineId,
          nextQuantityMilli,
          modifierOptionIds,
          lineVersion,
          cart.version
        )
      );
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const formatMoney = (minor: number, currency?: string) =>
    formatMoneyMinor(
      minor,
      currency ?? cart?.currency ?? currentOrder?.currency ?? "AED",
      language === "ar" ? "ar-AE" : "en-AE"
    );

  const saveLabel = useMemo(() => {
    if (saveState === "saving") return text.saving;
    if (saveState === "saved") return text.saved;
    if (saveState === "error") return text.saveError;
    return text.autoSave;
  }, [saveState, text]);

  return (
    <div className="workspace">
      <div className="workspace__heading">
        <div>
          <div className="eyebrow">{context.tenantName}</div>
          <h1>{text.workspace}</h1>
          <p>{context.branchName} · {context.roleName}</p>
        </div>
        <StatusBadge tone={shift ? "success" : "warning"}>
          {shift ? text.shiftOpen : text.noShift}
        </StatusBadge>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          {error}
          <button type="button" onClick={() => setError("")}>×</button>
        </div>
      ) : null}

      <div className="workspace-grid">
        <div className="workspace-main">
          {!shift && runtimeSettings?.requireOpenShift !== false ? (
            <Card title={text.shift}>
              <form className="inline-form" onSubmit={openNewShift}>
                {runtimeSettings?.requireOpeningCash !== false ? (
                  <Field
                    data-testid="opening-cash"
                    label={text.openingCash}
                    type="number"
                    min="0"
                    step="1"
                    value={openingCash}
                    onChange={(event) => setOpeningCash(event.target.value)}
                  />
                ) : <span />}
                <Button data-testid="open-shift" type="submit">{text.openShift}</Button>
              </form>
            </Card>
          ) : (
            <Card title={text.newOrder}>
              <form className="order-form" onSubmit={startOrder}>
                <SelectField
                  data-testid="new-order-service-type"
                  label={text.serviceType}
                  value={newServiceType}
                  onChange={(event) => setNewServiceType(event.target.value as ServiceType)}
                >
                  {(runtimeSettings?.enabledServiceTypes ?? serviceTypes).map((type) => <option key={type}>{type}</option>)}
                </SelectField>
                <TextAreaField
                  data-testid="new-order-note"
                  label={text.note}
                  rows={3}
                  value={newNote}
                  onChange={(event) => setNewNote(event.target.value)}
                />
                <Button data-testid="create-order" type="submit">{text.create}</Button>
              </form>
            </Card>
          )}

          {currentOrder ? (
            <Card title={text.currentOrder} className="order-editor">
              <Stack>
                <div className="order-editor__meta">
                  <strong data-testid="current-order-number">{currentOrder.orderNumber}</strong>
                  <Inline>
                    <StatusBadge tone={saveState === "error" ? "danger" : saveState === "saving" ? "warning" : "success"}>
                      {saveLabel}
                    </StatusBadge>
                    <StatusBadge tone="info">
                      {formatMoney(cart?.totalMinor ?? currentOrder.totalMinor, cart?.currency)}
                    </StatusBadge>
                  </Inline>
                </div>

                <Grid columns={2}>
                  <SelectField
                    data-testid="current-order-service-type"
                    label={text.serviceType}
                    value={currentOrder.serviceType}
                    onChange={(event) => {
                      setCurrentOrder({
                        ...currentOrder,
                        serviceType: event.target.value as ServiceType
                      });
                      setDirty(true);
                    }}
                  >
                    {(runtimeSettings?.enabledServiceTypes ?? serviceTypes).map((type) => <option key={type}>{type}</option>)}
                  </SelectField>
                  <TextAreaField
                    data-testid="current-order-note"
                    label={text.note}
                    rows={3}
                    value={currentOrder.note ?? ""}
                    onChange={(event) => {
                      setCurrentOrder({ ...currentOrder, note: event.target.value });
                      setDirty(true);
                    }}
                  />
                </Grid>

                <div className="pos-sale-layout">
                  <div className="pos-catalog-browser">
                    <div className="pos-category-tabs">
                      {posCatalog?.categories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          className={selectedCategoryId === category.id ? "is-active" : ""}
                          onClick={() => setSelectedCategoryId(category.id)}
                        >
                          {language === "ar" ? category.nameAr : category.nameEn}
                        </button>
                      ))}
                    </div>

                    <div className="pos-product-grid" data-testid="pos-product-grid">
                      {posCatalog?.products
                        .filter((product) => !selectedCategoryId || product.categoryId === selectedCategoryId)
                        .map((product) => {
                          const defaultVariant = product.variants.find((variant) => variant.isDefault) ?? product.variants[0];
                          return (
                            <button
                              key={product.id}
                              type="button"
                              className="pos-product-card"
                              data-testid={`pos-product-${product.code}`}
                              onClick={() => openProduct(product)}
                            >
                              <span>
                                <strong>{language === "ar" ? product.nameAr : product.nameEn}</strong>
                                <small>{product.code}</small>
                              </span>
                              <strong>{formatMoney(defaultVariant?.unitPriceMinor ?? 0, posCatalog.currency)}</strong>
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  <Card title={language === "ar" ? "سلة الطلب" : "Order Cart"} className="pos-cart">
                    {cart?.lines.length ? (
                      <Stack>
                        {cart.lines.map((line) => (
                          <div className="pos-cart-line" key={line.id}>
                            <div className="pos-cart-line__details">
                              <strong>{language === "ar" ? line.productNameAr : line.productNameEn}</strong>
                              <small>
                                {language === "ar" ? line.variantNameAr : line.variantNameEn}
                                {line.modifiers.length
                                  ? ` · ${line.modifiers.map((item) => language === "ar" ? item.optionNameAr : item.optionNameEn).join(", ")}`
                                  : ""}
                              </small>
                              {line.promotionCode ? <StatusBadge tone="success">{line.promotionCode}</StatusBadge> : null}
                            </div>
                            <div>
                              <strong>{formatMoney(line.totalMinor, cart.currency)}</strong>
                              <div className="pos-quantity">
                                <button
                                  type="button"
                                  onClick={() => void changeLineQuantity(
                                    line.id,
                                    line.quantityMilli - quantityStepForLine(line.productId),
                                    line.version,
                                    line.modifiers.map((item) => item.modifierOptionId)
                                  )}
                                >−</button>
                                <span>{line.quantityMilli / 1000}</span>
                                <button
                                  type="button"
                                  onClick={() => void changeLineQuantity(
                                    line.id,
                                    line.quantityMilli + quantityStepForLine(line.productId),
                                    line.version,
                                    line.modifiers.map((item) => item.modifierOptionId)
                                  )}
                                >+</button>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="pos-totals">
                          <div className="pos-total-row"><span>{language === "ar" ? "المجموع قبل الخصم" : "Subtotal"}</span><strong>{formatMoney(cart.subtotalMinor, cart.currency)}</strong></div>
                          <div className="pos-total-row"><span>{language === "ar" ? "الخصم" : "Discount"}</span><strong>− {formatMoney(cart.discountMinor, cart.currency)}</strong></div>
                          <div className="pos-total-row"><span>{language === "ar" ? "الضريبة" : "Tax"}</span><strong>{formatMoney(cart.taxMinor, cart.currency)}</strong></div>
                          <div className="pos-total-row is-grand"><span>{language === "ar" ? "الإجمالي" : "Total"}</span><strong data-testid="cart-total">{formatMoney(cart.totalMinor, cart.currency)}</strong></div>
                        </div>
                      </Stack>
                    ) : (
                      <p className="empty-state">{language === "ar" ? "اختر منتجًا لإضافته إلى الطلب." : "Select a product to add it to the order."}</p>
                    )}
                  </Card>
                </div>

                <p className="hint">
                  {text.lastUpdated}: {new Date(currentOrder.updatedAt).toLocaleString(language === "ar" ? "ar-AE" : "en-AE")}
                </p>
              </Stack>
            </Card>
          ) : null}
        </div>

        <Card title={text.openOrders} className="open-orders">
          <div data-testid="open-orders-list">
            {orders.length === 0 ? <p className="empty-state">{text.noOrders}</p> : null}
            {orders.map((order) => (
              <article className="order-row" key={order.id}>
                <div>
                  <strong>{order.orderNumber}</strong>
                  <span>{order.serviceType} · {order.status}</span>
                </div>
                <Button
                  data-testid={`restore-order-${order.id}`}
                  variant="secondary"
                  type="button"
                  onClick={() => void restoreOrder(order.id)}
                >
                  {text.restore}
                </Button>
              </article>
            ))}
          </div>
        </Card>
      </div>

      <Dialog
        open={Boolean(configProduct)}
        onClose={() => setConfigProduct(null)}
        title={configProduct ? (language === "ar" ? configProduct.nameAr : configProduct.nameEn) : ""}
        description={language === "ar" ? "اختر المتغير والإضافات قبل إضافة المنتج." : "Choose the variant and modifiers before adding the product."}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setConfigProduct(null)}>
              {language === "ar" ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              data-testid="add-configured-product"
              disabled={!configVariantId}
              onClick={() => void addConfiguredProduct()}
            >
              {language === "ar" ? "إضافة للطلب" : "Add to order"}
            </Button>
          </Inline>
        }
      >
        {configProduct ? (
          <Stack>
            <SelectField
              label={language === "ar" ? "المتغير" : "Variant"}
              value={configVariantId}
              onChange={(event) => setConfigVariantId(event.target.value)}
            >
              {configProduct.variants.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {language === "ar" ? variant.nameAr : variant.nameEn} · {formatMoney(variant.unitPriceMinor, posCatalog?.currency)}
                </option>
              ))}
            </SelectField>

            {configProduct.allowOpenPrice ? (
              <Field
                label={language === "ar" ? "السعر اليدوي" : "Manual price"}
                type="text"
                inputMode="decimal"
                suffix={currentOrder?.currency ?? posCatalog?.currency ?? "AED"}
                value={manualPriceInput}
                onChange={(event) => setManualPriceInput(event.target.value)}
              />
            ) : null}

            <div className="product-config-groups">
              {configProduct.modifierGroups.map((group) => (
                <Card
                  key={group.id}
                  title={language === "ar" ? group.nameAr : group.nameEn}
                  description={`${group.minimumSelections}-${group.maximumSelections}`}
                >
                  <Stack>
                    {group.options.map((option) => {
                      const checked = configOptionIds.includes(option.id);
                      return (
                        <div className="product-config-option" key={option.id}>
                          <Checkbox
                            label={language === "ar" ? option.nameAr : option.nameEn}
                            checked={checked}
                            onChange={(event) => {
                              const groupOptionIds = group.options.map((item) => item.id);
                              const selectedInGroup = configOptionIds.filter((id) => groupOptionIds.includes(id));
                              let next = configOptionIds;
                              if (event.target.checked) {
                                if (group.maximumSelections === 1) {
                                  next = configOptionIds.filter((id) => !groupOptionIds.includes(id));
                                } else if (selectedInGroup.length >= group.maximumSelections) {
                                  return;
                                }
                                next = [...next, option.id];
                              } else {
                                next = next.filter((id) => id !== option.id);
                              }
                              setConfigOptionIds(next);
                            }}
                          />
                          <strong>{option.priceDeltaMinor ? `+ ${formatMoney(option.priceDeltaMinor, posCatalog?.currency)}` : "—"}</strong>
                        </div>
                      );
                    })}
                  </Stack>
                </Card>
              ))}
            </div>
          </Stack>
        ) : null}
      </Dialog>

    </div>
  );
}
